import { Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import type { ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import type { ISmartrrOrderLineItem } from "@smartrr/shared/entities/OrderLineItem";
import type { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { SellingPlanPricingPolicyAdjustmentType } from "@smartrr/shared/shopifyGraphQL/api";
import { selectPricingPolicyCycleAdjustmentForCycle } from "@smartrr/shared/utils/getDiscounts";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React from "react";
import { calculateDiscountPercentage } from "../utils";

export const OrderHistorySavedPricingInfo = ({
  subscription,
  orderItems,
  order,
}: {
  subscription: IPurchaseState | undefined;
  orderItems: ISmartrrOrderLineItem[];
  order: ISmartrrOrderFromDb;
}) => {
  const { orderCycleIndex } = order;
  const orderNumber = orderCycleIndex ?? 0;
  const correspondingLineItem = subscription?.stLineItems.find(
    stLineItem => stLineItem.vnt.shopifyId === orderItems[0].vnt?.shopifyId
  );

  const isPrepaid = order.sts.some(st => isCPSPrepaid(st.schedule));

  const pricingPolicyAdjustment = correspondingLineItem?.pricingPolicy
    ? selectPricingPolicyCycleAdjustmentForCycle(correspondingLineItem.pricingPolicy, orderNumber)
    : undefined;

  /**
   * Calculating discount percentage from the prices given instead
   * of using pricing policy since we don't have access to accurate order number data.
   */
  const discountPercentage = calculateDiscountPercentage(+correspondingLineItem?.basePrice, +orderItems[0].price);
  const hasSellingPlanDiscount = correspondingLineItem?.basePrice !== orderItems[0].price;

  return (
    <React.Fragment>
      {!!hasSellingPlanDiscount &&
        !isPrepaid &&
        !!pricingPolicyAdjustment &&
        pricingPolicyAdjustment.adjustmentType === SellingPlanPricingPolicyAdjustmentType.Percentage &&
        pricingPolicyAdjustment.adjustmentValue.percentage > 0 && (
          <Stack direction="row" justifyContent="flex-end">
            <Typography
              variant="caption1"
              sx={{ opacity: "70%" }}
              className={prefixClassName("saved-amount-label")}
            >
              {typedLocalize("orderHistory.savedBySubscription", {
                amount: discountPercentage,
              })}
            </Typography>
          </Stack>
        )}
    </React.Fragment>
  );
};
